// utils/helpers/helper.generateData.js
import React from "react";
import moment from "moment";
import { i18n } from "engine/Contexts/AppProviders";

// helper
import {
  STATUSES_LIST_OPTIONS,
  DAY_OF_WEEK_OPTIONS,
  ABS_TYPES_LIST_OPTIONS,
  ABS_TYPES_LIST_WITH_LABEL_OPTIONS,
  TYPES_LIST_OPTIONS,
  LEVELS_OPTIONS,
  ACTIVITY_REASON_OPTIONS,
} from "utils/helpers/helper.constant";
import { sortByKey, getObjId, getArrayIds } from "utils/helpers/helper.general";
import { getUniqueArrayValue } from "utils/helpers/helper.filter-sort";
import {
  dateFormat, // DD.MM.YYYY
  dateFormatCompare, // DD-MM-YYYY
} from "utils/moment/moment.constant";
import {
  secondsToTimeLabel,
  isSameDates,
  calculateBusinessDays,
} from "utils/moment/moment.utils";

export const getEnviromentLabelName = env => {
  switch (env) {
    case "local":
      return "local";
    case "development":
      return "dev";
    case "staging":
      return "demo";
    case "production":
      return "";
  }
};

export const getEnviromentLabel = () => {
  const envLabelName = getEnviromentLabelName(process.env.BASE_ENV);
  return (
    !!envLabelName && (
      <span className={`env-label ${envLabelName}`}>{envLabelName}</span>
    )
  );
};

export const getAbsenceTypeLabel = type => {
  const current = ABS_TYPES_LIST_OPTIONS()?.find(abs => abs?.key == type);
  return (current?.title || current?.label)?.toLocaleLowerCase();
};
export const getAbsenceTypeLabelFull = type => {
  const current = ABS_TYPES_LIST_WITH_LABEL_OPTIONS()?.find(abs => abs?.key == type);
  return (current?.title || current?.label);
};

export const getAbsenceStatusLabel = type => {
  const current = STATUSES_LIST_OPTIONS()?.find(abs => abs?.key == type);
  return current?.title || current?.label;
};

export const getStatusesList = type => {
  let tempStatuses = STATUSES_LIST_OPTIONS();
  if (type == "sickness") {
    return tempStatuses?.filter(
      elem => elem?.key == "sickness-finish" || elem?.key == "sick",
    );
  } else if (type == "unjustified") {
    return tempStatuses?.filter(elem => elem?.key == "unjustified");
  } else {
    return tempStatuses?.filter(
      elem => elem?.key !== "sickness-finish" && elem?.key !== "sick",
    );
  }
};

export const getTypeList = () => ABS_TYPES_LIST_OPTIONS();

export const getUndertakeLabel = undertake => {
  switch (undertake) {
    case "with-parent":
      return i18n.t("absences.undertakeWithParent");
    case "alone":
      return i18n.t("absences.undertakeAlone");
  }
};

export const getAbsenceReason = element => {
  if (element?.type == "no-medical") {
    const currentReason = ACTIVITY_REASON_OPTIONS()?.find(
      r =>
        (r?.key == element?.reason ||
          r?.value == element?.reason ||
          r?.label == element?.reason ||
          r?.title == element?.reason) &&
        r,
    );
    return (
      currentReason?.title || currentReason?.label || currentReason?.value
    );
  } else {
    return getAbsenceTypeLabel(element?.type);
  }
};

export const getLevelLabel = number => {
  const current = LEVELS_OPTIONS()?.find(l => ((l?.key == number) || (l?.value == number)) && l);
  return current?.label || current?.title || current?.value;
};

export const getDayOfWeekObj = dayKey => {
  const list = DAY_OF_WEEK_OPTIONS();
  const current = list?.find(el => (
    el?.key == dayKey || el?.value == dayKey || el?.label == dayKey || el?.title == dayKey
  ));
  return current?.title || current?.label || current?.value;
};

export const generateAbsenceReason = absence => {
  if (!!absence) {
    switch (absence?.type) {
      case "sickness":
        if (absence?.status == "sick") {
          return (
            <span className="label-tag label-tag--sick">{i18n.t("absences.absReasonSick")}</span>
          );
        } else if (absence?.status == "sickness-finish") {
          return (
            <span className="label-tag label-tag--sick">{i18n.t("absences.absReasonSickFinish")}</span>
          );
        } else {
          return "";
        }

      case "medical":
        return (
          <span className={`label-tag label-tag--${absence?.status}`}>
            {`${getAbsenceStatusLabel(
              absence?.status,
            )} - ${getAbsenceTypeLabel(absence?.type)}`}
          </span>
        );
      case "no-medical":
        return (
          <span className={`label-tag label-tag--${absence?.status}`}>
            {`${getAbsenceStatusLabel(
              absence?.status,
            )} - ${getAbsenceTypeLabel(absence?.type)} ${
              absence?.reason ? `(${absence?.reason})` : ""
            }`}
          </span>
        );
      case "unjustified":
        return (
          <span className={`label-tag label-tag--${absence?.status}`}>
            {`${getAbsenceStatusLabel(absence?.status)}`}
          </span>
        );
      default:
        return "";
    }
  } else {
    return "";
  }
};

/**
 * Dates & Time
 */

// sort by: week, start, end, totalSec
export const getAbsenceCoursesTime = (element, sort = "start") => {
  const timeRange = [];

  if (element?.courses?.length > 0) {
    element?.courses.map(course => {
      const st = moment(course?.course_time_start)
        .format("HH:mm")
        .toString();
      const fn = moment(course?.course_time_end)
        .format("HH:mm")
        .toString();
      const [sthours, stminutes] = st.split(":");
      const [fnhours, fnminutes] = fn.split(":");

      const startSeconds = +sthours * 60 * 60 + +stminutes * 60;
      const endSeconds = +fnhours * 60 * 60 + +fnminutes * 60;
      const final = endSeconds - startSeconds;

      timeRange.push({
        from: moment(course?.course_time_start, dateFormat),
        to: moment(course?.course_time_end, dateFormat),
        week: course?.course_week,
        start: st,
        end: fn,
        startSec: startSeconds,
        endSec: endSeconds,
        totalSec: final,
        duration: secondsToTimeLabel(final),
      });
    });

    timeRange.sort(sortByKey(sort));
  }
  return timeRange;
};

export const getAbsenceMedMoreRange = (element, sort = "start") => {
  let timeRange = false;

  if (element) {
    const dateFrom = element?.date_from;
    const timeFrom = element?.time_from;
    const dateTo = element?.date_to;
    const timeTo = element?.time_to;

    let startD = moment(dateFrom);
    let endD = moment(dateTo);

    // Difference in number of days
    const days = calculateBusinessDays(startD, endD);

    const st = moment(timeFrom)
      .format("HH:mm")
      .toString();
    const fn = moment(timeTo)
      .format("HH:mm")
      .toString();
    const [sthours, stminutes] = st.split(":");
    const [fnhours, fnminutes] = fn.split(":");

    const startSeconds = +sthours * 60 * 60 + +stminutes * 60;
    const endSeconds = +fnhours * 60 * 60 + +fnminutes * 60;
    const final = endSeconds - startSeconds;

    timeRange = {
      from: moment(dateFrom).format(dateFormat),
      to: moment(dateTo).format(dateFormat),
      weekStart: moment(dateFrom).format("dd"),
      weekEnd: moment(dateTo).format("dd"),
      days: days,
      start: st,
      end: fn,
      startSec: startSeconds,
      endSec: endSeconds,
      totalSec: final,
      duration: secondsToTimeLabel(final),
    };
  }
  return timeRange;
};

export const getAbsenceDuration = element => {
  const absenceDuration = getAbsenceTimeDurationLabel(element); // timeDuration
  return absenceDuration;
};

export const getAbsenceStartTime = element => {
  return element?.time_from;
};

export const getAbsenceMainDate = element => {
  return element?.date_from;
};
export const getAbsenceTimeDurationLabel = element => {
  return !!element?.timeDuration && secondsToTimeLabel(element?.timeDuration);
};

export const getExcusedAbsenceStudents = (students = [], absences = []) => {
  const studentList = students || [];

  let excusedStds = [];
  if (studentList?.length > 0) {
    excusedStds =
      studentList?.length > 0 &&
      studentList?.filter(std => {
        if (absences?.length > 0) {
          const hasAbs =
            absences?.filter(
              abs => getObjId(abs?.student) == getObjId(std) && abs,
            ) || [];

          return hasAbs && hasAbs?.length > 0
            ? { ...std, hasAbs: hasAbs }
            : false;
        } else {
          return false;
        }
      });
  }
  return excusedStds ? excusedStds : [];
};

export const getExcusedStudents = (
  students = [],
  absences = [],
  attendances = [],
) => {
  const studentList = students || [];
  let excused = [];
  if (studentList?.length > 0) {
    const originalAbs = absences?.filter(a => a?.status != "refused") || [];
    excused = getExcusedAbsenceStudents(students, absences) || [];

    if (attendances?.length > 0) {
      const currentAttendanceArr = attendances;

      const missingStudents = currentAttendanceArr?.[0]?.missing_students
        ? currentAttendanceArr?.[0]?.missing_students
        : [];

      excused = [...new Set([...excused, ...missingStudents])];
    }
  }
  return excused;
};

export const getExcusedAbsencesByStudent = (students = [], absences = []) => {
  if (students?.length > 0 && absences?.length > 0) {
    const excusedAbsences = [];
    const map = new Map();

    students?.length > 0 &&
      students?.map(std => {
        if (absences?.length > 0) {
          const hasAbs =
            absences?.filter(
              abs => getObjId(abs?.student) == getObjId(std) && abs,
            ) || [];

          map.set(hasAbs, true);
          excusedAbsences.push(...hasAbs);
          return hasAbs && hasAbs?.length > 0 ? hasAbs : null;
        } else {
          return null;
        }
      });

    return excusedAbsences;
  } else {
    return [];
  }
};

export const getAttendancesOnDate = (course, onDate) => {
  if (!!course && !!onDate) {
    return (
      (course?.attendances?.length > 0 &&
        course?.attendances?.filter(
          att =>
            isSameDates(onDate, att?.date) &&
            getObjId(att?.course) == getObjId(course) &&
            att,
        )) ||
      []
    );
  } else {
    return [];
  }
};
export const getFromAttendancesOnDate = (attendances, courseID, onDate) => {
  if (!!attendances && !!courseID && !!onDate) {
    return (
      (attendances?.length > 0 &&
        attendances?.filter(
          att =>
            isSameDates(onDate, att?.date) &&
            getObjId(att?.course) == getObjId(courseID) &&
            att,
        )) ||
      []
    );
  } else {
    return [];
  }
};

/* Communication */

export const getCommunicationItemStatusClass = communication => {
  let classStatus = "";

  if (communication?.status != "draft") {
    if (communication?.types && communication?.types?.length > 0) {
      const uniqueType = getUniqueArrayValue(communication?.types);
      if (uniqueType.includes("appointments") || uniqueType.includes("forms")) {
        classStatus = "status__attachments";
      } else if (uniqueType.includes("no-type")) {
        classStatus = "status__no-type";
      } else if (uniqueType.includes("documents")) {
        classStatus = "status__documents";
      } else if (uniqueType.includes("discussion")) {
        classStatus = "status__discussion";
      }
    } else {
      classStatus = "status__no-type";
    }
  }
  return `${communication?.status} ${classStatus}`;
};

export const getCommunicationItemStatus = communication => {
  switch (communication?.status) {
    case "draft":
      return i18n.t("labels.draft");

    case "sent":
      if (communication?.types && communication?.types?.length > 0) {
        const uniqueType = getUniqueArrayValue(communication?.types);
        if (
          uniqueType.includes("appointments") ||
          uniqueType.includes("forms")
        ) {
          return `${i18n.t("common.sent")} - ${i18n.t("news.answers")} : ${communication?.studentsAnsweredAmount ||
            communication?.studentsAnswered?.length ||
            0}/${communication?.studentsAmount || 0}`;
        } else if (
          uniqueType.includes("no-type") ||
          uniqueType.includes("documents")
        ) {
          return `${i18n.t("common.sent")} - ${i18n.t("news.opening")} : ${communication?.studentsReadAmount ||
            0}/${communication?.studentsAmount || 0}`;
        }
      } else {
        return `${i18n.t("common.sent")} - ${i18n.t("news.opening")} : ${communication?.studentsReadAmount ||
          0}/${communication?.studentsAmount || 0}`;
      }
  }
};

export const getCommunicationStatusForFormAppoint = communication => {
  if (communication?.types && communication?.types?.length > 0) {
    const uniqueType = getUniqueArrayValue(communication?.types);
    if (uniqueType.includes("appointments") || uniqueType.includes("forms")) {
      return `${i18n.t("common.sent")} - ${i18n.t("news.answers")} : ${communication?.studentsAnsweredAmount ||
        communication?.studentsAnswered?.length ||
        0}/${communication?.studentsAmount || 0}`;
    }
  } else {
    return "";
  }
};

export const getCommunicationType = communication => {
  return (
    (communication?.types && getUniqueArrayValue(communication?.types)) || []
  );
};

export const getCommunicationTypeLabel = typeKey => {
  const list = TYPES_LIST_OPTIONS();
  const resultObj = list?.find(
    el => {
      return ((el?.key == typeKey) || (el?.value == typeKey)) && el
    }
  );
  return resultObj || {};
};

export const getCommunicationAudienceAsString = communication => {
  let audienceArray = [];
  if (communication?.audience) {
    if (communication?.audience?.school) {
      audienceArray.push(i18n.t("labels.all-school"));
    } else {
      if (communication?.audience?.classes) {
        communication?.audience?.classes?.map(classItem => {
          classItem?.title && audienceArray.push(classItem?.title);
        });
      }
      if (communication?.audience?.courses) {
        communication?.audience?.courses?.map(courseItem => {
          if (courseItem?._id) {
            const courseClasses =
              courseItem?.classes?.length > 0 &&
              courseItem?.classes
                ?.map(classInfo => classInfo?.title)
                ?.join(", ");
            const courseName = `${courseItem?.subject?.name}${
              courseItem?.course_code ? ` • ${courseItem?.course_code}` : ""
            }${!!courseClasses ? ` • ${courseClasses}` : ""}`;
            audienceArray.push(courseName);
          }
        });
      }
      if (communication?.audience?.students) {
        communication?.audience?.students?.map(stdItem => {
          if (stdItem?._id) {
            const stdName = `${stdItem?.description?.lastname} ${
              stdItem?.description?.firstname
            }`;
            audienceArray.push(stdName);
          }
        });
      }
      if (communication?.audience?.users) {
        communication?.audience?.users?.map(stdItem => {
          if (stdItem?._id) {
            const stdName = `${stdItem?.description?.lastname} ${
              stdItem?.description?.firstname
            }`;
            audienceArray.push(stdName);
          }
        });
      }
      if (communication?.audience?.audiences) {
        communication?.audience?.audiences?.map(classItem => {
          classItem?.title && audienceArray.push(classItem?.title);
        });
      }
    }
  }
  return audienceArray?.length > 0 ? audienceArray.join(", ") : "";
};

export const generateAudienceIncludes = initVal => {
  let resArr = [];
  if (initVal && Object.keys(initVal)?.length > 0 && initVal?.audience) {
    const audience = initVal?.audience;
    const classes =
      (audience?.classes && getArrayIds(audience?.classes)) || [];
    const courses =
      (audience?.courses && getArrayIds(audience?.courses)) || [];
    const users = (audience?.users && getArrayIds(audience?.users)) || [];
    const audiences =
      (audience?.audiences && getArrayIds(audience?.audiences)) || [];
    const students =
      (audience?.students && getArrayIds(audience?.students)) || [];
    resArr =
      classes && classes?.length > 0 ? resArr.concat(classes) : resArr;
    resArr =
      courses && courses?.length > 0 ? resArr.concat(courses) : resArr;
    resArr =
      students && students?.length > 0 ? resArr.concat(students) : resArr;
    resArr = users && users?.length > 0 ? resArr.concat(users) : resArr;
    resArr =
      audiences && audiences?.length > 0 ? resArr.concat(audiences) : resArr;
  } else if (initVal && Object.keys(initVal)?.length > 0) {
    const classes = (initVal?.classes && getArrayIds(initVal?.classes)) || [];
    const courses = (initVal?.courses && getArrayIds(initVal?.courses)) || [];
    const users = (initVal?.users && getArrayIds(initVal?.users)) || [];
    const audiences =
      (initVal?.audiences && getArrayIds(initVal?.audiences)) || [];
    const students =
      (initVal?.students && getArrayIds(initVal?.students)) || [];
    resArr =
      classes && classes?.length > 0 ? resArr.concat(classes) : resArr;
    resArr =
      courses && courses?.length > 0 ? resArr.concat(courses) : resArr;
    resArr = users && users?.length > 0 ? resArr.concat(users) : resArr;
    resArr =
      audiences && audiences?.length > 0
        ? resArr.concat(audiences)
        : resArr;
    resArr =
      students && students?.length > 0 ? resArr.concat(students) : resArr;
  }
  return resArr || [];
};

export const getResponseNeedAnswerTypes = function(types, answer) {
  const hasRequiredTypes =
    Array.isArray(types) &&
    types?.some(el => el == "forms" || el == "appointments");

  let needAnswers =
    types?.filter(el => el == "forms" || el == "appointments") || [];

  let result = {
    actionRequired: !!hasRequiredTypes && answer === null,
    needAnswers: [],
    hasAnswers: [],
  };

  if (!!hasRequiredTypes && answer != null) {
    const answerTypes = Object.keys(answer);

    if (!!answerTypes) {
      needAnswers =
        Array.isArray(types) &&
        types?.length > 0 &&
        types.filter(
          el =>
            (el == "forms" || el == "appointments") &&
            !answerTypes.includes(el),
        );
    }

    result = {
      needAnswers: needAnswers,
      hasAnswers: answerTypes,
      actionRequired: needAnswers?.length > 0,
    };
  } else if (!!hasRequiredTypes) {
    result = {
      needAnswers: needAnswers,
      hasAnswers: [],
      actionRequired: !!hasRequiredTypes && answer === null,
    };
  }

  return result;
};
